import { photoForPlacement } from "../photoPlacement.js";
import { useBandPhotos } from "./useBrandAssets.js";
import { useMediaObjectUrl } from "./useMedia.js";

/**
 * The band press photo behind the Marketing overview hero: whichever FILLED slot holds the
 * **Marketing Page** placement — explicitly assigned in Assets → Brand, or by its slot default (Main Photo 2,
 * slot 2). Added with the page-named placement vocabulary (PO, 2026-09-06; ART-95), which is also what put a
 * photo on this hero at all: `DarkHero` has carried a `photoUrl` slot since the Spotlight extraction and
 * `MarketingOverviewHero` simply never passed one, so it always fell back to the plain gradient.
 *
 * The SOCIALS reach hero reads this same placement (server-side, `ContentReadService`) — one choice by the
 * band, two heroes, per the same ruling. If you ever split them, split the placement too: a second consumer
 * silently sharing a first one's choice is the divergence this module exists to prevent.
 *
 * A sibling of {@link useSpotlightPhoto} / {@link useMusicHeroPhoto} rather than a placement parameter on
 * either: the three are five lines each and share their whole resolution rule via `photoForPlacement`, so
 * there is no logic to drift — only the placement name differs.
 *
 * Resolution goes through {@link useMediaObjectUrl}, not the payload `url` alone: `TryPublicUrl` yields NULL
 * wherever no public media host is configured (every local dev box), and the authenticated `/raw` path is the
 * app-wide degrade. Full size rather than a rendition — `sm` caps at 720px, which is soft across a hero that
 * runs the full content width.
 *
 * Returns `null` while resolving, when no photo holds the placement, and when the bytes fail — the hero then
 * keeps its gradient-only look rather than flashing a placeholder or a broken image.
 */
export function useMarketingHeroPhoto(): string | null {
  const q = useBandPhotos();
  const photos = q.data?.photos;
  const chosen = photos && photoForPlacement(photos, "Marketing Page");
  const media = useMediaObjectUrl(chosen?.mediaId, chosen?.url);
  return media.url ?? null;
}
